import { AIModule } from '../types';

export const REINFORCEMENT_LEARNING_FUNDAMENTALS_MODULES: AIModule[] = [
  {
    id: 'rl-intro',
    title: 'Introduction to Reinforcement Learning',
    description: 'Understanding the fundamentals of RL, agents, environments, and the reward-based learning paradigm.',
    duration: '2 hours',
    difficulty: 'beginner',
    content: `
# Introduction to Reinforcement Learning

## What is Reinforcement Learning?

Reinforcement Learning (RL) is a machine learning paradigm where an agent learns to make decisions by taking actions in an environment to maximize cumulative reward. Unlike supervised learning, RL doesn't require labeled data but learns through trial and error.

## Key Components of RL

### 1. Agent
The learner or decision maker that takes actions in the environment.

### 2. Environment
The world in which the agent operates and receives feedback.

### 3. State (S)
The current situation or configuration of the environment.

### 4. Action (A)
The set of possible moves the agent can make.

### 5. Reward (R)
The feedback signal that indicates how good or bad an action was.

### 6. Policy (π)
The strategy that the agent uses to determine actions based on states.

## The RL Process

1. Agent observes current state
2. Agent selects an action based on policy
3. Environment transitions to new state
4. Agent receives reward
5. Agent updates its policy
6. Process repeats

## Applications

- Game playing (Chess, Go, video games)
- Robotics control
- Trading algorithms
- Recommendation systems
- Autonomous vehicles
`,
    objectives: [
      'Understand the core concepts of reinforcement learning',
      'Identify the key components of RL systems',
      'Distinguish RL from other machine learning paradigms',
      'Recognize real-world applications of RL'
    ],
    codeExamples: [
      {
        id: 'basic-rl-setup',
        title: 'Basic RL Environment Setup',
        description: 'Simple grid world environment example',
        code: `
import numpy as np
import random

class GridWorld:
    def __init__(self, size=4):
        self.size = size
        self.state = (0, 0)  # Start position
        self.goal = (size-1, size-1)  # Goal position
        
    def reset(self):
        self.state = (0, 0)
        return self.state
    
    def step(self, action):
        # Actions: 0=up, 1=right, 2=down, 3=left
        x, y = self.state
        
        if action == 0 and x > 0:  # Up
            x -= 1
        elif action == 1 and y < self.size - 1:  # Right
            y += 1
        elif action == 2 and x < self.size - 1:  # Down
            x += 1
        elif action == 3 and y > 0:  # Left
            y -= 1
            
        self.state = (x, y)
        
        # Reward calculation
        if self.state == self.goal:
            reward = 10  # Reached goal
            done = True
        else:
            reward = -1  # Step penalty
            done = False
            
        return self.state, reward, done
    
    def get_valid_actions(self):
        actions = []
        x, y = self.state
        if x > 0: actions.append(0)  # Up
        if y < self.size - 1: actions.append(1)  # Right
        if x < self.size - 1: actions.append(2)  # Down
        if y > 0: actions.append(3)  # Left
        return actions

# Example usage
env = GridWorld(4)
state = env.reset()
print(f"Initial state: {state}")

for step in range(10):
    valid_actions = env.get_valid_actions()
    action = random.choice(valid_actions)
    state, reward, done = env.step(action)
    print(f"Step {step+1}: Action {action}, State {state}, Reward {reward}")
    
    if done:
        print("Goal reached!")
        break
`,
        language: 'python',
        category: 'demo'
      }
    ],
    quiz: {
      questions: [
        {
          id: 'rl-definition',
          question: 'What is the key characteristic that distinguishes reinforcement learning from supervised learning?',
          options: [
            'RL uses labeled training data',
            'RL learns through trial and error with reward signals',
            'RL only works with numerical data',
            'RL requires less computational power'
          ],
          correctAnswer: 1,
          explanation: 'Reinforcement learning learns through trial and error using reward signals, without requiring labeled training data like supervised learning.'
        },
        {
          id: 'rl-components',
          question: 'Which of the following is NOT a core component of a reinforcement learning system?',
          options: [
            'Agent',
            'Environment', 
            'Training labels',
            'Reward signal'
          ],
          correctAnswer: 2,
          explanation: 'Training labels are used in supervised learning, not reinforcement learning. RL systems learn from reward signals instead.'
        }
      ]
    }
  },
  {
    id: 'markov-decision-processes',
    title: 'Markov Decision Processes (MDPs)',
    description: 'Understanding MDPs as the mathematical framework for modeling RL problems.',
    duration: '2.5 hours',
    difficulty: 'intermediate',
    content: `
# Markov Decision Processes (MDPs)

## Introduction

A Markov Decision Process provides a mathematical framework for modeling decision making in situations where outcomes are partly random and partly under the control of a decision maker.

## Formal Definition

An MDP is defined by a tuple (S, A, P, R, γ) where:

- **S**: Set of states
- **A**: Set of actions  
- **P**: Transition probability function P(s'|s,a)
- **R**: Reward function R(s,a,s')
- **γ**: Discount factor (0 ≤ γ ≤ 1)

## The Markov Property

The Markov property states that the future is independent of the past given the present:

P(S_{t+1} = s' | S_t = s, A_t = a, S_{t-1}, A_{t-1}, ..., S_0, A_0) = P(S_{t+1} = s' | S_t = s, A_t = a)

This means the next state depends only on the current state and action, not on the entire history.

## Value Functions

### State Value Function V^π(s)
The expected return starting from state s following policy π:

V^π(s) = E_π[G_t | S_t = s]

where G_t is the return (cumulative discounted reward).

### Action Value Function Q^π(s,a)
The expected return starting from state s, taking action a, then following policy π:

Q^π(s,a) = E_π[G_t | S_t = s, A_t = a]

## Bellman Equations

### Bellman Equation for V^π
V^π(s) = Σ_a π(a|s) Σ_{s'} P(s'|s,a)[R(s,a,s') + γV^π(s')]

### Bellman Equation for Q^π  
Q^π(s,a) = Σ_{s'} P(s'|s,a)[R(s,a,s') + γ Σ_{a'} π(a'|s')Q^π(s',a')]

## Optimal Value Functions

The optimal state value function V*(s) is the maximum value function over all policies:
V*(s) = max_π V^π(s)

The optimal action value function Q*(s,a) is:
Q*(s,a) = max_π Q^π(s,a)
`,
    objectives: [
      'Define and understand Markov Decision Processes',
      'Apply the Markov property to RL problems',
      'Calculate value functions for given policies',
      'Understand Bellman equations and their significance'
    ],
    codeExamples: [
      {
        id: 'mdp-implementation',
        title: 'MDP Implementation and Value Iteration',
        description: 'Implementing an MDP and solving it using value iteration',
        code: `
import numpy as np

class MDP:
    def __init__(self, states, actions, transitions, rewards, discount=0.9):
        self.states = states
        self.actions = actions
        self.transitions = transitions  # P(s'|s,a)
        self.rewards = rewards  # R(s,a,s')
        self.discount = discount
        self.num_states = len(states)
        self.num_actions = len(actions)
    
    def value_iteration(self, theta=1e-6):
        """Solve MDP using value iteration algorithm"""
        V = np.zeros(self.num_states)
        
        while True:
            delta = 0
            V_old = V.copy()
            
            for s in range(self.num_states):
                # Calculate value for each action
                action_values = []
                for a in range(self.num_actions):
                    value = 0
                    for s_next in range(self.num_states):
                        prob = self.transitions[s][a][s_next]
                        reward = self.rewards[s][a][s_next]
                        value += prob * (reward + self.discount * V_old[s_next])
                    action_values.append(value)
                
                # Take maximum over actions
                V[s] = max(action_values)
                delta = max(delta, abs(V[s] - V_old[s]))
            
            if delta < theta:
                break
        
        return V
    
    def extract_policy(self, V):
        """Extract optimal policy from value function"""
        policy = np.zeros(self.num_states, dtype=int)
        
        for s in range(self.num_states):
            action_values = []
            for a in range(self.num_actions):
                value = 0
                for s_next in range(self.num_states):
                    prob = self.transitions[s][a][s_next]
                    reward = self.rewards[s][a][s_next]
                    value += prob * (reward + self.discount * V[s_next])
                action_values.append(value)
            
            policy[s] = np.argmax(action_values)
        
        return policy

# Example: Simple Grid World MDP
def create_grid_world_mdp():
    # 2x2 grid: states 0,1,2,3 with state 3 as goal
    states = [0, 1, 2, 3]
    actions = [0, 1, 2, 3]  # up, right, down, left
    
    # Initialize transition probabilities
    transitions = np.zeros((4, 4, 4))
    rewards = np.zeros((4, 4, 4))
    
    # Define transitions for non-terminal states
    # State 0 (top-left)
    transitions[0][1][1] = 1.0  # right -> state 1
    transitions[0][2][2] = 1.0  # down -> state 2
    transitions[0][0][0] = 1.0  # up -> stay (wall)
    transitions[0][3][0] = 1.0  # left -> stay (wall)
    
    # State 1 (top-right)  
    transitions[1][2][3] = 1.0  # down -> state 3 (goal)
    transitions[1][3][0] = 1.0  # left -> state 0
    transitions[1][0][1] = 1.0  # up -> stay (wall)
    transitions[1][1][1] = 1.0  # right -> stay (wall)
    
    # State 2 (bottom-left)
    transitions[2][1][3] = 1.0  # right -> state 3 (goal)
    transitions[2][0][0] = 1.0  # up -> state 0
    transitions[2][2][2] = 1.0  # down -> stay (wall)
    transitions[2][3][2] = 1.0  # left -> stay (wall)
    
    # State 3 (goal) - terminal state
    for a in range(4):
        transitions[3][a][3] = 1.0
    
    # Set rewards
    rewards[1][2][3] = 10  # Reaching goal from state 1
    rewards[2][1][3] = 10  # Reaching goal from state 2
    # Small negative reward for other transitions
    for s in range(3):  # Non-terminal states
        for a in range(4):
            for s_next in range(4):
                if transitions[s][a][s_next] > 0 and rewards[s][a][s_next] == 0:
                    rewards[s][a][s_next] = -1
    
    return MDP(states, actions, transitions, rewards)

# Solve the MDP
mdp = create_grid_world_mdp()
optimal_values = mdp.value_iteration()
optimal_policy = mdp.extract_policy(optimal_values)

print("Optimal Values:")
for i, v in enumerate(optimal_values):
    print(f"State {i}: {v:.2f}")

print("\\nOptimal Policy:")
action_names = ['Up', 'Right', 'Down', 'Left']
for i, a in enumerate(optimal_policy):
    print(f"State {i}: {action_names[a]}")
`,
        language: 'python',
        category: 'tutorial'
      }
    ]
  },
  {
    id: 'dynamic-programming',
    title: 'Dynamic Programming in RL',
    description: 'Policy evaluation, policy improvement, and value iteration algorithms.',
    duration: '3 hours',
    difficulty: 'intermediate',
    content: `
# Dynamic Programming in Reinforcement Learning

## Overview

Dynamic Programming (DP) provides a collection of algorithms that can be used to compute optimal policies given a perfect model of the environment as a Markov Decision Process (MDP).

## Key Assumptions

1. **Perfect model**: Complete knowledge of the MDP (transitions and rewards)
2. **Finite state and action spaces**
3. **Optimal substructure**: Optimal solutions contain optimal sub-solutions

## Policy Evaluation

Policy evaluation computes the state-value function V^π for a given policy π.

### Algorithm:
1. Initialize V(s) arbitrarily for all s
2. Repeat until convergence:
   - For each state s:
     V(s) ← Σ_a π(a|s) Σ_{s'} P(s'|s,a)[R(s,a,s') + γV(s')]

## Policy Improvement

Given a value function V^π, we can find a better policy π' by acting greedily:

π'(s) = argmax_a Σ_{s'} P(s'|s,a)[R(s,a,s') + γV^π(s')]

## Policy Iteration

Combines policy evaluation and policy improvement:

1. Initialize policy π arbitrarily
2. Repeat:
   a. Policy Evaluation: Compute V^π
   b. Policy Improvement: Improve π using V^π
3. Stop when policy is stable

## Value Iteration

Directly finds the optimal value function:

1. Initialize V(s) arbitrarily
2. Repeat until convergence:
   V(s) ← max_a Σ_{s'} P(s'|s,a)[R(s,a,s') + γV(s')]
3. Extract optimal policy

## Convergence Properties

- **Policy Iteration**: Guaranteed to converge to optimal policy in finite steps
- **Value Iteration**: Converges to optimal value function exponentially fast
- **Computational Complexity**: O(|S|²|A|) per iteration
`,
    objectives: [
      'Implement policy evaluation algorithms',
      'Understand policy improvement theorem',
      'Compare policy iteration vs value iteration',
      'Analyze convergence properties of DP algorithms'
    ],
    codeExamples: [
      {
        id: 'policy-iteration',
        title: 'Policy Iteration Implementation',
        description: 'Complete implementation of policy iteration algorithm',
        code: `
import numpy as np

class PolicyIteration:
    def __init__(self, mdp, theta=1e-6):
        self.mdp = mdp
        self.theta = theta
        self.num_states = mdp.num_states
        self.num_actions = mdp.num_actions
    
    def policy_evaluation(self, policy):
        """Evaluate a given policy"""
        V = np.zeros(self.num_states)
        
        while True:
            delta = 0
            V_old = V.copy()
            
            for s in range(self.num_states):
                v = 0
                action = policy[s]
                
                for s_next in range(self.num_states):
                    prob = self.mdp.transitions[s][action][s_next]
                    reward = self.mdp.rewards[s][action][s_next]
                    v += prob * (reward + self.mdp.discount * V_old[s_next])
                
                V[s] = v
                delta = max(delta, abs(V[s] - V_old[s]))
            
            if delta < self.theta:
                break
        
        return V
    
    def policy_improvement(self, V):
        """Improve policy given value function"""
        policy = np.zeros(self.num_states, dtype=int)
        
        for s in range(self.num_states):
            action_values = []
            
            for a in range(self.num_actions):
                value = 0
                for s_next in range(self.num_states):
                    prob = self.mdp.transitions[s][a][s_next]
                    reward = self.mdp.rewards[s][a][s_next]
                    value += prob * (reward + self.mdp.discount * V[s_next])
                action_values.append(value)
            
            policy[s] = np.argmax(action_values)
        
        return policy
    
    def run(self):
        """Run complete policy iteration algorithm"""
        # Initialize random policy
        policy = np.random.randint(0, self.num_actions, self.num_states)
        
        iteration = 0
        while True:
            iteration += 1
            print(f"Policy Iteration {iteration}")
            
            # Policy Evaluation
            V = self.policy_evaluation(policy)
            
            # Policy Improvement
            new_policy = self.policy_improvement(V)
            
            # Check for convergence
            if np.array_equal(policy, new_policy):
                print(f"Converged after {iteration} iterations")
                break
            
            policy = new_policy
        
        return policy, V

# Example usage with previous grid world
mdp = create_grid_world_mdp()
pi = PolicyIteration(mdp)
optimal_policy, optimal_values = pi.run()

print("\\nFinal Results:")
print("Optimal Policy:", optimal_policy)
print("Optimal Values:", optimal_values.round(2))
`,
        language: 'python',
        category: 'tutorial'
      }
    ],
    practice: {
      task: 'Implement a modified policy iteration algorithm that alternates between partial policy evaluation (limited iterations) and policy improvement.',
      hints: [
        'Limit policy evaluation to a fixed number of iterations instead of full convergence',
        'This can lead to faster convergence in practice',
        'Compare performance with full policy iteration'
      ]
    }
  },
  {
    id: 'monte-carlo-methods',
    title: 'Monte Carlo Methods',
    description: 'Model-free learning using Monte Carlo sampling and estimation techniques.',
    duration: '3 hours',
    difficulty: 'intermediate',
    content: `
# Monte Carlo Methods in Reinforcement Learning

## Introduction

Monte Carlo (MC) methods learn directly from experience without requiring a model of the environment. They use sample returns to estimate value functions and policies.

## Key Characteristics

- **Model-free**: No knowledge of MDP transitions required
- **Sample-based**: Learn from complete episodes
- **Unbiased estimates**: Returns are unbiased estimates of true values
- **High variance**: Individual samples can vary significantly

## Monte Carlo Policy Evaluation

Estimate V^π(s) by averaging returns following visits to state s:

V^π(s) = average of returns following visits to s

### Two Approaches:

1. **First-visit MC**: Average returns only after first visit to s in each episode
2. **Every-visit MC**: Average returns after every visit to s

## Monte Carlo Control

### On-Policy MC Control (using ε-greedy)

1. Initialize Q(s,a) arbitrarily
2. Initialize policy π as ε-greedy with respect to Q
3. For each episode:
   - Generate episode using π
   - For each state-action pair (s,a) in episode:
     - Update Q(s,a) using sample return
   - Update π to be ε-greedy with respect to Q

### Off-Policy MC Control

Uses importance sampling to learn about target policy while following behavior policy.

## Importance Sampling

When learning off-policy, we need to account for the difference between target and behavior policies:

ρ_t = ∏_{k=t}^{T-1} [π(A_k|S_k) / b(A_k|S_k)]

Where π is target policy and b is behavior policy.

## Advantages and Limitations

### Advantages:
- Simple conceptually
- Can learn from actual experience
- No bootstrapping
- Unbiased value estimates

### Limitations:
- Requires episodic tasks
- High variance estimates
- Slow convergence
- Must wait until episode completion
`,
    objectives: [
      'Implement Monte Carlo policy evaluation',
      'Understand on-policy vs off-policy learning',
      'Apply importance sampling for off-policy methods',
      'Compare MC methods with dynamic programming'
    ],
    codeExamples: [
      {
        id: 'mc-control',
        title: 'Monte Carlo Control Implementation',
        description: 'On-policy Monte Carlo control with ε-greedy exploration',
        code: `
import numpy as np
import random
from collections import defaultdict

class MonteCarloControl:
    def __init__(self, env, epsilon=0.1, discount=0.9):
        self.env = env
        self.epsilon = epsilon
        self.discount = discount
        self.Q = defaultdict(lambda: defaultdict(float))
        self.returns = defaultdict(lambda: defaultdict(list))
        self.policy = defaultdict(int)
    
    def epsilon_greedy_policy(self, state):
        """ε-greedy action selection"""
        valid_actions = self.env.get_valid_actions()
        
        if random.random() < self.epsilon:
            return random.choice(valid_actions)
        else:
            # Greedy action
            if state in self.Q:
                q_values = {a: self.Q[state][a] for a in valid_actions}
                return max(q_values, key=q_values.get)
            else:
                return random.choice(valid_actions)
    
    def generate_episode(self):
        """Generate a complete episode using current policy"""
        episode = []
        state = self.env.reset()
        
        while True:
            action = self.epsilon_greedy_policy(state)
            next_state, reward, done = self.env.step(action)
            
            episode.append((state, action, reward))
            
            if done:
                break
            
            state = next_state
        
        return episode
    
    def update_q_values(self, episode):
        """Update Q-values using episode returns"""
        # Calculate returns for each step
        returns = []
        G = 0
        
        # Work backwards through episode
        for step in reversed(episode):
            state, action, reward = step
            G = reward + self.discount * G
            returns.append((state, action, G))
        
        # Reverse to get forward order
        returns.reverse()
        
        # Update Q-values (first-visit MC)
        visited = set()
        
        for state, action, G in returns:
            state_action = (state, action)
            
            if state_action not in visited:
                visited.add(state_action)
                
                # Add return to list
                self.returns[state][action].append(G)
                
                # Update Q-value as average of returns
                self.Q[state][action] = np.mean(self.returns[state][action])
    
    def train(self, num_episodes):
        """Train the agent using Monte Carlo control"""
        episode_rewards = []
        
        for episode_num in range(num_episodes):
            # Generate episode
            episode = self.generate_episode()
            
            # Calculate total reward
            total_reward = sum(reward for _, _, reward in episode)
            episode_rewards.append(total_reward)
            
            # Update Q-values
            self.update_q_values(episode)
            
            # Decay epsilon
            if episode_num % 100 == 0:
                self.epsilon *= 0.99
                print(f"Episode {episode_num}, Average Reward: {np.mean(episode_rewards[-100:]):.2f}, ε: {self.epsilon:.3f}")
        
        return episode_rewards
    
    def get_optimal_policy(self):
        """Extract optimal policy from Q-values"""
        policy = {}
        
        for state in self.Q:
            valid_actions = list(self.Q[state].keys())
            if valid_actions:
                policy[state] = max(self.Q[state], key=self.Q[state].get)
        
        return policy

# Example usage
class SimpleGridWorld:
    def __init__(self, size=4):
        self.size = size
        self.reset()
        
    def reset(self):
        self.state = (0, 0)
        return self.state
    
    def get_valid_actions(self):
        x, y = self.state
        actions = []
        if x > 0: actions.append(0)  # Up
        if y < self.size - 1: actions.append(1)  # Right  
        if x < self.size - 1: actions.append(2)  # Down
        if y > 0: actions.append(3)  # Left
        return actions
    
    def step(self, action):
        x, y = self.state
        
        if action == 0 and x > 0: x -= 1  # Up
        elif action == 1 and y < self.size - 1: y += 1  # Right
        elif action == 2 and x < self.size - 1: x += 1  # Down
        elif action == 3 and y > 0: y -= 1  # Left
        
        self.state = (x, y)
        
        # Reward structure
        if self.state == (self.size-1, self.size-1):
            return self.state, 10, True  # Goal reached
        else:
            return self.state, -1, False  # Step penalty

# Train the agent
env = SimpleGridWorld(4)
mc_agent = MonteCarloControl(env, epsilon=0.1)
rewards = mc_agent.train(5000)

# Display results
optimal_policy = mc_agent.get_optimal_policy()
print("\\nOptimal Policy:")
action_names = ['Up', 'Right', 'Down', 'Left']
for state, action in sorted(optimal_policy.items()):
    print(f"State {state}: {action_names[action]}")
`,
        language: 'python',
        category: 'tutorial'
      }
    ]
  },
  {
    id: 'temporal-difference-learning',
    title: 'Temporal Difference Learning',
    description: 'TD(0), SARSA, and Q-learning algorithms for model-free RL.',
    duration: '3.5 hours',
    difficulty: 'intermediate',
    content: `
# Temporal Difference Learning

## Introduction

Temporal Difference (TD) learning combines ideas from Monte Carlo and Dynamic Programming methods. TD methods can learn directly from experience without a model and update estimates before the end of an episode.

## Key Advantages

- **Online learning**: Updates during episodes, not just at the end
- **Lower variance**: Bootstraps from current estimates
- **Works with continuing tasks**: Doesn't require episodes to terminate
- **Faster convergence**: Often converges faster than MC methods

## TD Prediction (TD(0))

Updates value estimates using the TD error:

V(S_t) ← V(S_t) + α[R_{t+1} + γV(S_{t+1}) - V(S_t)]

The quantity in brackets is called the **TD error**:
δ_t = R_{t+1} + γV(S_{t+1}) - V(S_t)

## TD Control Methods

### SARSA (On-Policy TD Control)

Updates Q-values using the current policy:

Q(S_t, A_t) ← Q(S_t, A_t) + α[R_{t+1} + γQ(S_{t+1}, A_{t+1}) - Q(S_t, A_t)]

**Algorithm:**
1. Choose action A_t using policy (e.g., ε-greedy)
2. Take action A_t, observe R_{t+1}, S_{t+1}
3. Choose next action A_{t+1} using policy
4. Update Q(S_t, A_t)
5. S_t ← S_{t+1}, A_t ← A_{t+1}

### Q-Learning (Off-Policy TD Control)

Updates Q-values using the greedy policy:

Q(S_t, A_t) ← Q(S_t, A_t) + α[R_{t+1} + γ max_a Q(S_{t+1}, a) - Q(S_t, A_t)]

**Key Difference**: Q-learning learns the optimal Q-function regardless of the policy being followed.

## Expected SARSA

A variant that uses expected value instead of sample:

Q(S_t, A_t) ← Q(S_t, A_t) + α[R_{t+1} + γ Σ_a π(a|S_{t+1})Q(S_{t+1}, a) - Q(S_t, A_t)]

## Comparison of Methods

| Method | Type | Converges to | Variance | Exploration |
|--------|------|-------------|----------|-------------|
| SARSA | On-policy | Q^π | Lower | Conservative |
| Q-learning | Off-policy | Q* | Higher | Aggressive |
| Expected SARSA | Off-policy | Q* | Lower | Balanced |

## Learning Rate Schedules

For convergence guarantees, learning rates should satisfy:
- Σ_t α_t = ∞ (infinite sum)
- Σ_t α_t² < ∞ (finite sum of squares)

Common choices:
- α_t = 1/t
- α_t = 1/(1 + visits to state-action pair)
- Constant α (works well in practice)
`,
    objectives: [
      'Implement TD(0) prediction algorithm',
      'Understand the difference between on-policy and off-policy methods',
      'Compare SARSA vs Q-learning behavior',
      'Analyze convergence properties of TD methods'
    ],
    codeExamples: [
      {
        id: 'q-learning',
        title: 'Q-Learning Implementation',
        description: 'Complete Q-learning algorithm with ε-greedy exploration',
        code: `
import numpy as np
import random
from collections import defaultdict
import matplotlib.pyplot as plt

class QLearningAgent:
    def __init__(self, env, learning_rate=0.1, discount=0.9, epsilon=0.1):
        self.env = env
        self.alpha = learning_rate
        self.gamma = discount
        self.epsilon = epsilon
        self.Q = defaultdict(lambda: defaultdict(float))
    
    def get_action(self, state, training=True):
        """ε-greedy action selection"""
        valid_actions = self.env.get_valid_actions()
        
        if training and random.random() < self.epsilon:
            return random.choice(valid_actions)
        else:
            # Greedy action
            if state in self.Q:
                q_values = {a: self.Q[state][a] for a in valid_actions}
                return max(q_values, key=q_values.get)
            else:
                return random.choice(valid_actions)
    
    def update(self, state, action, reward, next_state, done):
        """Q-learning update rule"""
        # Current Q-value
        current_q = self.Q[state][action]
        
        # Next state max Q-value (0 if terminal)
        if done:
            next_max_q = 0
        else:
            next_actions = self.env.get_valid_actions_for_state(next_state)
            if next_actions and next_state in self.Q:
                next_max_q = max(self.Q[next_state][a] for a in next_actions)
            else:
                next_max_q = 0
        
        # Q-learning update
        target = reward + self.gamma * next_max_q
        self.Q[state][action] = current_q + self.alpha * (target - current_q)
    
    def train(self, num_episodes):
        """Train the Q-learning agent"""
        episode_rewards = []
        episode_lengths = []
        
        for episode in range(num_episodes):
            state = self.env.reset()
            total_reward = 0
            steps = 0
            
            while True:
                # Choose action
                action = self.get_action(state, training=True)
                
                # Take action
                next_state, reward, done = self.env.step(action)
                
                # Update Q-table
                self.update(state, action, reward, next_state, done)
                
                # Update tracking variables
                total_reward += reward
                steps += 1
                state = next_state
                
                if done or steps > 1000:  # Prevent infinite episodes
                    break
            
            episode_rewards.append(total_reward)
            episode_lengths.append(steps)
            
            # Decay epsilon
            if episode % 100 == 0:
                self.epsilon = max(0.01, self.epsilon * 0.995)
                avg_reward = np.mean(episode_rewards[-100:])
                avg_length = np.mean(episode_lengths[-100:])
                print(f"Episode {episode}: Avg Reward: {avg_reward:.2f}, Avg Length: {avg_length:.1f}, ε: {self.epsilon:.3f}")
        
        return episode_rewards, episode_lengths
    
    def get_policy(self):
        """Extract policy from Q-table"""
        policy = {}
        for state in self.Q:
            valid_actions = list(self.Q[state].keys())
            if valid_actions:
                policy[state] = max(self.Q[state], key=self.Q[state].get)
        return policy

class SARSAAgent:
    def __init__(self, env, learning_rate=0.1, discount=0.9, epsilon=0.1):
        self.env = env
        self.alpha = learning_rate
        self.gamma = discount
        self.epsilon = epsilon
        self.Q = defaultdict(lambda: defaultdict(float))
    
    def get_action(self, state):
        """ε-greedy action selection"""
        valid_actions = self.env.get_valid_actions()
        
        if random.random() < self.epsilon:
            return random.choice(valid_actions)
        else:
            if state in self.Q:
                q_values = {a: self.Q[state][a] for a in valid_actions}
                return max(q_values, key=q_values.get)
            else:
                return random.choice(valid_actions)
    
    def update(self, state, action, reward, next_state, next_action, done):
        """SARSA update rule"""
        current_q = self.Q[state][action]
        
        if done:
            next_q = 0
        else:
            next_q = self.Q[next_state][next_action]
        
        # SARSA update
        target = reward + self.gamma * next_q
        self.Q[state][action] = current_q + self.alpha * (target - current_q)
    
    def train(self, num_episodes):
        """Train the SARSA agent"""
        episode_rewards = []
        
        for episode in range(num_episodes):
            state = self.env.reset()
            action = self.get_action(state)
            total_reward = 0
            steps = 0
            
            while True:
                # Take action
                next_state, reward, done = self.env.step(action)
                next_action = self.get_action(next_state) if not done else None
                
                # Update Q-table
                self.update(state, action, reward, next_state, next_action, done)
                
                total_reward += reward
                steps += 1
                
                if done or steps > 1000:
                    break
                
                # Move to next state-action pair
                state = next_state
                action = next_action
            
            episode_rewards.append(total_reward)
            
            if episode % 100 == 0:
                self.epsilon = max(0.01, self.epsilon * 0.995)
                print(f"SARSA Episode {episode}: Avg Reward: {np.mean(episode_rewards[-100:]):.2f}")
        
        return episode_rewards

# Enhanced environment for comparison
class CliffWalk:
    def __init__(self):
        self.height = 4
        self.width = 12
        self.start = (3, 0)
        self.goal = (3, 11)
        self.cliff = [(3, i) for i in range(1, 11)]
        self.reset()
    
    def reset(self):
        self.state = self.start
        return self.state
    
    def get_valid_actions(self):
        return [0, 1, 2, 3]  # up, right, down, left
    
    def get_valid_actions_for_state(self, state):
        return [0, 1, 2, 3]
    
    def step(self, action):
        x, y = self.state
        
        # Move based on action
        if action == 0 and x > 0: x -= 1  # up
        elif action == 1 and y < self.width - 1: y += 1  # right
        elif action == 2 and x < self.height - 1: x += 1  # down
        elif action == 3 and y > 0: y -= 1  # left
        
        self.state = (x, y)
        
        # Check rewards
        if self.state == self.goal:
            return self.state, 0, True
        elif self.state in self.cliff:
            self.state = self.start  # Fall off cliff, return to start
            return self.state, -100, False
        else:
            return self.state, -1, False

# Compare Q-learning vs SARSA
env = CliffWalk()

# Train Q-learning agent
q_agent = QLearningAgent(env, learning_rate=0.1, epsilon=0.1)
q_rewards, _ = q_agent.train(500)

# Train SARSA agent  
env = CliffWalk()  # Reset environment
sarsa_agent = SARSAAgent(env, learning_rate=0.1, epsilon=0.1)
sarsa_rewards = sarsa_agent.train(500)

print("\\nTraining completed!")
print(f"Q-learning final performance: {np.mean(q_rewards[-100:]):.2f}")
print(f"SARSA final performance: {np.mean(sarsa_rewards[-100:]):.2f}")
`,
        language: 'python',
        category: 'tutorial'
      }
    ],
    practice: {
      task: 'Implement Expected SARSA and compare its performance with Q-learning and SARSA on the CliffWalk environment.',
      hints: [
        'Expected SARSA uses the expected value of next state Q-values',
        'Calculate expectation using current policy probabilities',
        'Should have lower variance than regular SARSA'
      ]
    }
  },
  {
    id: 'function-approximation',
    title: 'Function Approximation',
    description: 'Using neural networks and other function approximators for large state spaces.',
    duration: '4 hours',
    difficulty: 'advanced',
    content: `
# Function Approximation in Reinforcement Learning

## The Need for Function Approximation

As state spaces become large or continuous, tabular methods become impractical:
- Memory requirements: O(|S||A|) for Q-tables
- Learning time: Need to visit every state-action pair
- Generalization: No learning transfer between similar states

## Function Approximation Approaches

### 1. Linear Function Approximation
Represent value function as linear combination of features:

V(s) = θ₁φ₁(s) + θ₂φ₂(s) + ... + θₙφₙ(s) = θᵀφ(s)

Where φ(s) is a feature vector and θ are learnable parameters.

### 2. Neural Network Approximation
Use neural networks to approximate value functions:
- Deep Q-Networks (DQN) for Q(s,a)
- Policy networks for π(a|s)
- Value networks for V(s)

## Gradient-Based Methods

### Semi-Gradient TD(0)
For value function approximation:

θ_{t+1} = θ_t + α[R_{t+1} + γV(S_{t+1}, θ_t) - V(S_t, θ_t)]∇V(S_t, θ_t)

### Semi-Gradient SARSA
For action-value approximation:

θ_{t+1} = θ_t + α[R_{t+1} + γQ(S_{t+1}, A_{t+1}, θ_t) - Q(S_t, A_t, θ_t)]∇Q(S_t, A_t, θ_t)

## Deep Q-Networks (DQN)

Key innovations:
1. **Experience Replay**: Store transitions and sample randomly
2. **Target Networks**: Use separate network for targets
3. **Convolutional Layers**: For high-dimensional inputs (images)

### DQN Algorithm:
1. Initialize Q-network and target network
2. For each episode:
   - Select action using ε-greedy policy
   - Store transition in replay buffer
   - Sample random batch from buffer
   - Compute target: r + γ max_a' Q_target(s', a')
   - Update Q-network using gradient descent
   - Periodically update target network

## Challenges with Function Approximation

### 1. Deadly Triad
Combination of three factors can cause instability:
- Function approximation
- Bootstrapping (using estimates to update estimates)
- Off-policy learning

### 2. Divergence Issues
- Function approximation can cause value functions to diverge
- Breaking of convergence guarantees from tabular methods

### 3. Feature Design
- Quality of features crucial for linear methods
- Neural networks can learn features automatically

## Stability Techniques

1. **Experience Replay**: Decorrelates training data
2. **Target Networks**: Stabilizes learning targets
3. **Double Q-Learning**: Reduces overestimation bias
4. **Dueling Networks**: Separates state value and advantage
5. **Prioritized Experience Replay**: Focus on important transitions
`,
    objectives: [
      'Understand when function approximation is necessary',
      'Implement linear function approximation for RL',
      'Build a basic Deep Q-Network',
      'Recognize stability challenges and solutions'
    ],
    codeExamples: [
      {
        id: 'linear-fa',
        title: 'Linear Function Approximation',
        description: 'Linear value function approximation with gradient descent',
        code: `
import numpy as np
import matplotlib.pyplot as plt

class LinearValueApproximation:
    def __init__(self, feature_size, learning_rate=0.01):
        self.theta = np.random.randn(feature_size) * 0.1
        self.alpha = learning_rate
    
    def features(self, state):
        """Create feature vector from state"""
        # Example: polynomial features for 1D state
        if isinstance(state, (int, float)):
            # Polynomial features: [1, s, s², s³, ...]
            return np.array([1, state, state**2, state**3, state**4])
        elif hasattr(state, '__len__') and len(state) == 2:
            # 2D grid world features
            x, y = state
            return np.array([1, x, y, x*y, x**2, y**2])
        else:
            # Default: one-hot encoding for discrete states
            features = np.zeros(len(self.theta))
            if state < len(features):
                features[state] = 1
            return features
    
    def value(self, state):
        """Compute value function V(s) = θᵀφ(s)"""
        phi = self.features(state)
        return np.dot(self.theta, phi)
    
    def gradient(self, state):
        """Compute gradient ∇V(s) = φ(s)"""
        return self.features(state)
    
    def update(self, state, target):
        """Update parameters using gradient descent"""
        current_value = self.value(state)
        error = target - current_value
        grad = self.gradient(state)
        
        # Gradient descent update
        self.theta += self.alpha * error * grad
        
        return error

class TDLambdaLinear:
    def __init__(self, feature_size, learning_rate=0.01, discount=0.9, lambda_param=0.9):
        self.value_fn = LinearValueApproximation(feature_size, learning_rate)
        self.gamma = discount
        self.lambda_param = lambda_param
        self.eligibility_trace = np.zeros(feature_size)
    
    def reset_trace(self):
        """Reset eligibility trace for new episode"""
        self.eligibility_trace = np.zeros_like(self.eligibility_trace)
    
    def update(self, state, reward, next_state, done):
        """TD(λ) update with linear function approximation"""
        # Current and next state values
        current_value = self.value_fn.value(state)
        next_value = 0 if done else self.value_fn.value(next_state)
        
        # TD error
        td_error = reward + self.gamma * next_value - current_value
        
        # Update eligibility trace
        grad = self.value_fn.gradient(state)
        self.eligibility_trace = self.gamma * self.lambda_param * self.eligibility_trace + grad
        
        # Update parameters
        self.value_fn.theta += self.value_fn.alpha * td_error * self.eligibility_trace
        
        return td_error

# Example: Mountain Car with Linear FA
class MountainCarLinear:
    def __init__(self):
        self.min_position = -1.2
        self.max_position = 0.6
        self.max_speed = 0.07
        self.goal_position = 0.5
        self.reset()
    
    def reset(self):
        self.position = np.random.uniform(-0.6, -0.4)
        self.velocity = 0
        return self.get_state()
    
    def get_state(self):
        return [self.position, self.velocity]
    
    def step(self, action):
        # action: 0=left, 1=nothing, 2=right
        force = (action - 1) * 0.001
        
        self.velocity += force + np.cos(3 * self.position) * (-0.0025)
        self.velocity = np.clip(self.velocity, -self.max_speed, self.max_speed)
        
        self.position += self.velocity
        self.position = np.clip(self.position, self.min_position, self.max_position)
        
        if self.position == self.min_position and self.velocity < 0:
            self.velocity = 0
        
        done = self.position >= self.goal_position
        reward = -1 if not done else 0
        
        return self.get_state(), reward, done

class LinearSARSA:
    def __init__(self, num_actions, feature_size, learning_rate=0.01, epsilon=0.1):
        self.num_actions = num_actions
        self.epsilon = epsilon
        # Separate theta for each action
        self.theta = np.random.randn(num_actions, feature_size) * 0.1
        self.alpha = learning_rate
        self.gamma = 0.9
    
    def features(self, state):
        """Create feature vector from state"""
        pos, vel = state
        # Radial basis functions
        positions = np.linspace(-1.2, 0.6, 10)
        velocities = np.linspace(-0.07, 0.07, 10)
        
        features = []
        for p in positions:
            for v in velocities:
                # Gaussian RBF
                feature = np.exp(-((pos - p)**2 + (vel - v)**2) / 0.1)
                features.append(feature)
        
        return np.array(features + [1])  # Add bias term
    
    def q_value(self, state, action):
        """Compute Q(s,a) = θₐᵀφ(s)"""
        phi = self.features(state)
        return np.dot(self.theta[action], phi)
    
    def get_action(self, state, training=True):
        """ε-greedy action selection"""
        if training and np.random.random() < self.epsilon:
            return np.random.randint(self.num_actions)
        else:
            q_values = [self.q_value(state, a) for a in range(self.num_actions)]
            return np.argmax(q_values)
    
    def update(self, state, action, reward, next_state, next_action, done):
        """SARSA update with linear function approximation"""
        current_q = self.q_value(state, action)
        
        if done:
            target = reward
        else:
            next_q = self.q_value(next_state, next_action)
            target = reward + self.gamma * next_q
        
        # Gradient descent update
        error = target - current_q
        grad = self.features(state)
        self.theta[action] += self.alpha * error * grad
        
        return error

# Training example
env = MountainCarLinear()
agent = LinearSARSA(num_actions=3, feature_size=101, learning_rate=0.1)

episode_lengths = []
for episode in range(1000):
    state = env.reset()
    action = agent.get_action(state)
    total_steps = 0
    
    while total_steps < 500:  # Max steps per episode
        next_state, reward, done = env.step(action)
        next_action = agent.get_action(next_state) if not done else None
        
        agent.update(state, action, reward, next_state, next_action, done)
        
        state = next_state
        action = next_action
        total_steps += 1
        
        if done:
            break
    
    episode_lengths.append(total_steps)
    
    if episode % 100 == 0:
        avg_length = np.mean(episode_lengths[-100:])
        print(f"Episode {episode}: Average length: {avg_length:.1f}")
        agent.epsilon *= 0.99

print(f"Final average episode length: {np.mean(episode_lengths[-100:]):.1f}")
`,
        language: 'python',
        category: 'tutorial'
      },
      {
        id: 'simple-dqn',
        title: 'Simple Deep Q-Network',
        description: 'Basic DQN implementation using neural networks',
        code: `
import numpy as np
import torch
import torch.nn as nn
import torch.optim as optim
import random
from collections import deque

class DQN(nn.Module):
    def __init__(self, input_size, hidden_size, output_size):
        super(DQN, self).__init__()
        self.network = nn.Sequential(
            nn.Linear(input_size, hidden_size),
            nn.ReLU(),
            nn.Linear(hidden_size, hidden_size),
            nn.ReLU(),
            nn.Linear(hidden_size, output_size)
        )
    
    def forward(self, x):
        return self.network(x)

class ReplayBuffer:
    def __init__(self, capacity):
        self.buffer = deque(maxlen=capacity)
    
    def push(self, state, action, reward, next_state, done):
        self.buffer.append((state, action, reward, next_state, done))
    
    def sample(self, batch_size):
        return random.sample(self.buffer, batch_size)
    
    def __len__(self):
        return len(self.buffer)

class DQNAgent:
    def __init__(self, state_size, action_size, learning_rate=0.001, epsilon=0.1, gamma=0.99):
        self.state_size = state_size
        self.action_size = action_size
        self.epsilon = epsilon
        self.gamma = gamma
        
        # Neural networks
        self.q_network = DQN(state_size, 64, action_size)
        self.target_network = DQN(state_size, 64, action_size)
        self.optimizer = optim.Adam(self.q_network.parameters(), lr=learning_rate)
        
        # Replay buffer
        self.memory = ReplayBuffer(10000)
        self.batch_size = 32
        self.update_target_freq = 100
        self.step_count = 0
        
        # Initialize target network
        self.update_target_network()
    
    def update_target_network(self):
        """Copy weights from main network to target network"""
        self.target_network.load_state_dict(self.q_network.state_dict())
    
    def get_action(self, state, training=True):
        """ε-greedy action selection"""
        if training and random.random() < self.epsilon:
            return random.randint(0, self.action_size - 1)
        
        state_tensor = torch.FloatTensor(state).unsqueeze(0)
        q_values = self.q_network(state_tensor)
        return q_values.argmax().item()
    
    def remember(self, state, action, reward, next_state, done):
        """Store experience in replay buffer"""
        self.memory.push(state, action, reward, next_state, done)
    
    def replay(self):
        """Train the network on a batch of experiences"""
        if len(self.memory) < self.batch_size:
            return
        
        # Sample batch
        batch = self.memory.sample(self.batch_size)
        states = torch.FloatTensor([e[0] for e in batch])
        actions = torch.LongTensor([e[1] for e in batch])
        rewards = torch.FloatTensor([e[2] for e in batch])
        next_states = torch.FloatTensor([e[3] for e in batch])
        dones = torch.BoolTensor([e[4] for e in batch])
        
        # Current Q values
        current_q_values = self.q_network(states).gather(1, actions.unsqueeze(1))
        
        # Next Q values from target network
        next_q_values = self.target_network(next_states).max(1)[0].detach()
        target_q_values = rewards + (self.gamma * next_q_values * ~dones)
        
        # Compute loss
        loss = nn.MSELoss()(current_q_values.squeeze(), target_q_values)
        
        # Optimize
        self.optimizer.zero_grad()
        loss.backward()
        self.optimizer.step()
        
        # Update target network periodically
        self.step_count += 1
        if self.step_count % self.update_target_freq == 0:
            self.update_target_network()
        
        # Decay epsilon
        self.epsilon = max(0.01, self.epsilon * 0.9995)

# Simple environment for testing
class SimpleEnv:
    def __init__(self, size=10):
        self.size = size
        self.reset()
    
    def reset(self):
        self.position = 0
        return [self.position / self.size]  # Normalized position
    
    def step(self, action):
        # Action 0: left, Action 1: right
        if action == 0:
            self.position = max(0, self.position - 1)
        else:
            self.position = min(self.size - 1, self.position + 1)
        
        # Reward: +1 for reaching the end, -0.1 for each step
        if self.position == self.size - 1:
            reward = 1
            done = True
        else:
            reward = -0.1
            done = False
        
        return [self.position / self.size], reward, done

# Training loop
env = SimpleEnv(10)
agent = DQNAgent(state_size=1, action_size=2, learning_rate=0.001)

episode_rewards = []
for episode in range(1000):
    state = env.reset()
    total_reward = 0
    
    for step in range(100):  # Max steps per episode
        action = agent.get_action(state)
        next_state, reward, done = env.step(action)
        
        agent.remember(state, action, reward, next_state, done)
        agent.replay()
        
        state = next_state
        total_reward += reward
        
        if done:
            break
    
    episode_rewards.append(total_reward)
    
    if episode % 100 == 0:
        avg_reward = np.mean(episode_rewards[-100:])
        print(f"Episode {episode}: Average reward: {avg_reward:.3f}, ε: {agent.epsilon:.3f}")

print("Training completed!")
print(f"Final average reward: {np.mean(episode_rewards[-100:]):.3f}")
`,
        language: 'python',
        category: 'tutorial'
      }
    ],
    practice: {
      task: 'Implement Double DQN to reduce overestimation bias in the simple DQN implementation.',
      hints: [
        'Use main network to select actions, target network to evaluate them',
        'Modify the target calculation in the replay method',
        'Compare performance with regular DQN'
      ]
    }
  }
];